import { useCallback, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Button } from "./ui";
import { CloseIcon } from "./icons";
import { useT } from "../i18n/index.js";

/**
 * Читает квадрат с камеры этого устройства.
 *
 * Появился он потому, что схема передачи ключа опиралась на системную камеру
 * телефона — она распознаёт QR и открывает ссылку, — а из приложения это никак
 * не следовало: экран замка говорил «наведите камеру», и навести её было
 * неоткуда.
 *
 * Разбор — `jsQR`, а не встроенный `BarcodeDetector`. Тот быстрее и ничего не
 * весит, но его нет в Safari, то есть ровно на половине телефонов, для которых
 * это писалось. Одна дорога, работающая везде, лучше двух, из которых вторая —
 * сообщение о том, что здесь не получится.
 */

/** Кадров в секунду. Больше не нужно — человек наводит камеру, а не целится. */
const SCAN_INTERVAL_MS = 120;

type Trouble = "denied" | "unavailable" | null;

export function QrScanner({
  onFound,
  onCancel,
}: {
  /** Возвращает `true`, если прочитанное подошло: иначе сканирование продолжается. */
  onFound: (text: string) => boolean;
  onCancel: () => void;
}) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [trouble, setTrouble] = useState<Trouble>(null);

  // В ref, а не в состоянии: цикл разбора живёт в эффекте, и пересоздавать его
  // на каждый рендер значило бы дёргать камеру.
  const found = useRef(onFound);
  found.current = onFound;

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setTrouble("unavailable");
      return;
    }

    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let alive = true;

    /*
     * Гасится в одном месте и при любом исходе.
     *
     * Камера, оставшаяся включённой после ухода с экрана, — это не утечка
     * производительности, а горящий индикатор и работающий микрофонный сосед в
     * приложении, которое обещает приватность. Поэтому остановка стоит в
     * cleanup, а не в обработчике успеха.
     */
    const stop = () => {
      alive = false;
      if (timer !== undefined) clearTimeout(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // Задняя камера там, где их две. `ideal`, а не `exact`: на ноутбуке
          // задней нет, и требовать её значило бы отказать вовсе.
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch {
        // Отказ в доступе и отсутствие камеры отсюда неразличимы, и ответ у них
        // один — показать, что делать дальше.
        if (alive) setTrouble("denied");
        return;
      }

      if (!alive || !videoRef.current) {
        stop();
        return;
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => undefined);

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });

      const tick = () => {
        if (!alive) return;
        const video = videoRef.current;

        if (video && context && video.videoWidth > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);

          const frame = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(frame.data, frame.width, frame.height, {
            inversionAttempts: "dontInvert",
          });

          // Только если прочитанное подошло: чужой квадрат в кадре не должен
          // закрывать сканер и заставлять начинать заново.
          if (code?.data && found.current(code.data)) {
            stop();
            return;
          }
        }

        timer = window.setTimeout(tick, SCAN_INTERVAL_MS);
      };

      tick();
    })();

    return stop;
  }, []);

  const cancel = useCallback(() => onCancel(), [onCancel]);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative overflow-hidden rounded-card bg-surface-alt">
        {trouble ? (
          <p className="px-4 py-8 text-center text-[0.875rem] text-muted">
            {t(trouble === "denied" ? "encryption.scanDenied" : "encryption.scanUnavailable")}
          </p>
        ) : (
          <>
            <video
              ref={videoRef}
              muted
              playsInline
              className="aspect-square w-full bg-surface-alt object-cover"
            />
            {/* Рамка, а не инструкция: куда наводить, показывается, а не
                объясняется. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-8 rounded-card outline-2 outline-offset-4 outline-accent/70"
            />
          </>
        )}
      </div>

      <p aria-live="polite" className="px-1 text-[0.875rem] text-muted">
        {trouble ? t("encryption.scanUseCode") : t("encryption.scanHint")}
      </p>

      <Button variant="secondary" full onClick={cancel}>
        <CloseIcon className="size-[18px]" strokeWidth={2} />
        {t("common.cancel")}
      </Button>
    </div>
  );
}
